import * as React from "react"
import { Badge } from "@/components/ui/badge"
import { LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils"

interface ActivityItemProps {
  icon: LucideIcon
  description: string
  company: string
  timestamp: string
  severity: "low" | "medium" | "high" | "critical"
  isLast?: boolean
}

const severityStyles = {
  low: {
    iconBg: "bg-green-100 dark:bg-green-900/30",
    iconColor: "text-green-600 dark:text-green-400",
    badge: "bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-400",
    label: "Low",
  },
  medium: {
    iconBg: "bg-yellow-100 dark:bg-yellow-900/30",
    iconColor: "text-yellow-600 dark:text-yellow-400",
    badge: "bg-yellow-100 text-yellow-700 hover:bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400",
    label: "Medium",
  },
  high: {
    iconBg: "bg-orange-100 dark:bg-orange-900/30",
    iconColor: "text-orange-600 dark:text-orange-400",
    badge: "bg-orange-100 text-orange-700 hover:bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400",
    label: "High",
  },
  critical: {
    iconBg: "bg-red-100 dark:bg-red-900/30",
    iconColor: "text-red-600 dark:text-red-400",
    badge: "bg-red-100 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400",
    label: "Critical",
  },
}

export function ActivityItem({ 
  icon: Icon, 
  description, 
  company, 
  timestamp, 
  severity,
  isLast = false 
}: ActivityItemProps) {
  const styles = severityStyles[severity]

  return (
    <div className={cn("flex items-start gap-3 py-3", !isLast && "border-b")}>
      <div className={cn("rounded-full p-2 mt-0.5", styles.iconBg)}>
        <Icon className={cn("h-4 w-4", styles.iconColor)} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium mb-1">{description}</p>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <span>{company}</span>
          <span>•</span>
          <span>{timestamp}</span>
        </div>
      </div>
      <Badge className={cn("shrink-0", styles.badge)}>
        {styles.label}
      </Badge>
    </div>
  )
}
